import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, ChevronRight, X, Plus } from "lucide-react";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";

const COLOMBIAN_DEPARTMENTS = [
  "Amazonas","Antioquia","Arauca","Atlántico","Bogotá D.C.","Bolívar","Boyacá",
  "Caldas","Caquetá","Casanare","Cauca","Cesar","Chocó","Córdoba","Cundinamarca",
  "Guainía","Guaviare","Huila","La Guajira","Magdalena","Meta","Nariño",
  "Norte de Santander","Putumayo","Quindío","Risaralda","San Andrés","Santander",
  "Sucre","Tolima","Valle del Cauca","Vaupés","Vichada",
];

const SERVICES_SUGGESTIONS = [
  "Construcción","Mantenimiento","Consultoría","Suministros","Tecnología",
  "Alimentación","Transporte","Salud","Educación","Seguridad","Aseo",
  "Comunicaciones","Ingeniería","Arquitectura","Medio ambiente",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [keywords, setKeywords] = useState([]);
  const [kwInput, setKwInput] = useState("");
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [svcInput, setSvcInput] = useState("");
  const [saving, setSaving] = useState(false);

  const addKeyword = () => {
    const kw = kwInput.trim();
    if (kw && !keywords.includes(kw)) setKeywords([...keywords, kw]);
    setKwInput("");
  };
  const addService = () => {
    const svc = svcInput.trim();
    if (svc && !services.includes(svc)) setServices([...services, svc]);
    setSvcInput("");
  };
  const toggleDept = (d) => setDepartments(prev =>
    prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
  );

  const save = async (skip = false) => {
    setSaving(true);
    try {
      if (!skip && (keywords.length || services.length || departments.length)) {
        await api.put("/profile", { keywords, services, departments });
      }
      await api.patch("/auth/onboarding-complete");
    } catch (e) { /* ignore */ }
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#080C14] flex flex-col items-center justify-start px-4 py-10">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-emerald-500/4 rounded-full blur-3xl" />
      </div>
      <div className="relative w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-500/15 border border-emerald-500/30 rounded-xl mb-3">
            <Target className="w-6 h-6 text-emerald-400" />
          </div>
          <h1 className="text-slate-100 text-2xl font-bold mb-1">Configura tu perfil</h1>
          <p className="text-slate-400 text-sm">RadarLit usará esta información para encontrar licitaciones relevantes para tu empresa.</p>
          <p className="text-slate-500 text-xs mt-1">Puedes modificar esto más adelante en "Perfil Empresarial".</p>
        </div>

        <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-6 space-y-6">
          {/* Keywords */}
          <div>
            <label className="text-slate-300 text-sm font-semibold mb-2 block">
              Palabras clave de tus servicios
              <span className="text-slate-500 text-xs font-normal ml-2">¿Qué ofreces? ej: "grass sintético", "jardinería"</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                value={kwInput} onChange={e => setKwInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                className="flex-1 bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-3 py-2 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                placeholder="Escribe y presiona Enter..."
                data-testid="onboarding-keyword-input"
              />
              <button onClick={addKeyword} className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 hover:bg-emerald-500/20 transition-colors">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {keywords.map(k => (
                <span key={k} className="inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs px-2.5 py-1 rounded-full">
                  {k}
                  <button onClick={() => setKeywords(prev => prev.filter(x => x !== k))} className="hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <label className="text-slate-300 text-sm font-semibold mb-2 block">
              Tipo de servicios
              <span className="text-slate-500 text-xs font-normal ml-2">Selecciona o escribe los tuyos</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {SERVICES_SUGGESTIONS.map(s => (
                <button key={s} onClick={() => { if (!services.includes(s)) setServices(p => [...p, s]); }}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${services.includes(s) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                  {s}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-1">
              <input
                value={svcInput} onChange={e => setSvcInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addService())}
                className="flex-1 bg-slate-800 border border-slate-700 text-slate-100 rounded-lg px-3 py-2 text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                placeholder="Otro servicio..."
              />
              <button onClick={addService} className="p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 hover:bg-emerald-500/20 transition-colors">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {services.filter(s => !SERVICES_SUGGESTIONS.includes(s)).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {services.filter(s => !SERVICES_SUGGESTIONS.includes(s)).map(s => (
                  <span key={s} className="inline-flex items-center gap-1 bg-slate-700 text-slate-300 text-xs px-2.5 py-1 rounded-full">
                    {s}<button onClick={() => setServices(p => p.filter(x => x !== s))} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Departments */}
          <div>
            <label className="text-slate-300 text-sm font-semibold mb-2 block">
              Departamentos de interés
              <span className="text-slate-500 text-xs font-normal ml-2">¿Dónde operas?</span>
            </label>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
              {COLOMBIAN_DEPARTMENTS.map(d => (
                <button key={d} onClick={() => toggleDept(d)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${departments.includes(d) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => save(false)} disabled={saving}
              data-testid="onboarding-save"
              className="flex-1 btn-primary rounded-lg py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? "Guardando..." : "Guardar y comenzar"}
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => save(true)} disabled={saving}
              className="px-4 py-2.5 text-slate-500 hover:text-slate-300 text-sm transition-colors">
              Saltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
